/* eslint-disable react-native/no-unused-styles -- porting from old code */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium, LabelSmall} from "@khanacademy/wonder-blocks-typography";

interface Props {
    /**
     * The main title rendered in larger bold text.
     */
    title: string;

    /**
     * The dialog subtitle.
     */
    subtitle?: string | React.ReactElement;

    /**
     * Optional identifier to use for the dialog title.
     *
     * Useful for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
}

/**
 * Renders a simplified version of WB's ModalHeader that allows for elements
 * to be rendered in the subtitle.
 */
export const ModalHeader = (props: Props): React.ReactElement => {
    const {subtitle, title, titleId} = props;

    return (
        <MediaLayout styleSheets={styleSheets}>
            {({styles}) => (
                <View style={[styles.header]}>
                    <HeadingMedium id={titleId} style={styles.title}>
                        {title}
                    </HeadingMedium>
                    {subtitle && (
                        <LabelSmall style={styles.subtitle}>
                            {subtitle}
                        </LabelSmall>
                    )}
                </View>
            )}
        </MediaLayout>
    );
};

const styleSheets = {
    all: StyleSheet.create({
        header: {
            boxShadow: `0px 1px 0px ${semanticColor.core.border.neutral.subtle}`,
            display: "flex",
            flexDirection: "column",
            minBlockSize: 66, // magic number mimicing WB's Modal Header style
            padding: `${spacing.large_24}px ${spacing.xLarge_32}px`,
            position: "relative",
            width: "100%",
        },

        breadcrumbs: {
            color: semanticColor.core.foreground.neutral.subtle,
            marginBlockEnd: spacing.xSmall_8,
        },

        title: {
            // Prevent title from overlapping the close button
            paddingInlineEnd: spacing.medium_16,
        },

        subtitle: {
            color: semanticColor.core.foreground.neutral.subtle,
            marginBlockStart: spacing.xSmall_8,
        },
    }),

    small: StyleSheet.create({
        header: {
            paddingInlineStart: spacing.medium_16,
            paddingInlineEnd: spacing.medium_16,
        },

        title: {
            paddingInlineEnd: spacing.xLarge_32,
        },
    }),
} as const;
